import { nanoid } from '@reduxjs/toolkit';
import { useAppSelector } from '~/store';
import { Services, MediaTypes } from '~/constants';
import { OldUploadDraftCb, OldUploadNotifiers } from '~/uploads/types';
import { getStillDataUriFromGif } from '~/utils/image/getStillDataUriFromGif';
import {
  selectImageDimensionsKey,
  selectS3Signature,
  selectUserId,
} from '~/user/selectors';

import { monitorUploadProgress } from './monitorUploadProgress';
import Uploader from './Uploader';

export const useOldUploaderCallback = ({
  randomId,
  passedNotifiers,
}: {
  randomId?: boolean;
  passedNotifiers?: OldUploadNotifiers;
} = {}): {
  oldUploadCallback: OldUploadDraftCb;
} => {
  const userId = useAppSelector(selectUserId);
  const s3UploadSignature = useAppSelector(selectS3Signature);
  const imageDimensionsKey = useAppSelector(selectImageDimensionsKey);

  const oldUploadCallback: OldUploadDraftCb = (
    id,
    file,
    uploadType,
    // eslint-disable-next-line default-param-last
    notifiers,
    source
  ) => {
    id = randomId ? nanoid() : id;
    notifiers = passedNotifiers || notifiers;
    if (!notifiers) {
      throw new Error(
        'notifiers are required directly or through passedNotifiers'
      );
    }

    const uploader = new Uploader({
      userId,
      s3UploadSignature: {
        ...s3UploadSignature,
        // this renaming happened inside composer userData importing,
        // it should be cleaned up as we refactor Composer importData utils
        base64Policy: s3UploadSignature.base64policy,
        successActionStatus: s3UploadSignature.success_action_status,
      },
      errorNotifier: notifiers?.queueError,
      imageDimensionsKey,
    });

    // @REVIEW: this will always be true when randomId is true (story-group-composer)
    //          consider defaulting to false
    const transcodeVideo = Services.get(id)?.transcodeVideo ?? true;

    notifiers?.uploadStarted({ id, uploaderInstance: uploader, file, source });

    uploader
      .upload(file, { transcodeVideo, draftId: id })
      .then((responseFromUpload) => {
        if (responseFromUpload.success === false) {
          notifiers?.queueError({
            message:
              'Uh oh! It looks like we had an issue connecting to our servers. Up for trying again?',
          });
        }

        switch (responseFromUpload.type) {
          case MediaTypes.IMAGE:
            notifiers?.uploadedDraftImage({
              id,
              uploaderInstance: uploader,
              url: responseFromUpload.url,
                location: window.location, //eslint-disable-line
              width: responseFromUpload.width,
              height: responseFromUpload.height,
              file,
              source,
            });
            break;

          case MediaTypes.VIDEO: {
            notifiers?.uploadedDraftVideo({
              id,
              uploaderInstance: uploader,
              uploadId: responseFromUpload.uploadId,
              file,
              transcodeVideo: responseFromUpload.transcodeVideo,
              videoMetadata: responseFromUpload.videoMetadata,
              name: responseFromUpload.name,
            });
            break;
          }

          case MediaTypes.GIF:
            getStillDataUriFromGif(responseFromUpload.url)
              .then((dataUri) => dataUri)
              .catch(() => null)
              .then((dataUriOrNull) => {
                notifiers?.draftGifUploaded({
                  id,
                  uploaderInstance: uploader,
                  url: responseFromUpload.url,
                  stillGifUrl: dataUriOrNull,
                  width: responseFromUpload.width,
                  height: responseFromUpload.height,
                  file,
                  source,
                });
              });
            break;

          default:
            break;
        }
      })
      .catch(() => {
        notifiers?.queueError({
          message:
            'Uh oh! It looks like we had an issue connecting to our servers. Up for trying again?',
        });
      });

    if (notifiers?.updateFileUploadProgress) {
      monitorUploadProgress({
        id,
        updateUploadProgress: notifiers.updateFileUploadProgress,
        uploaderInstance: uploader,
      });
    }
  };

  return { oldUploadCallback };
};
